import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  styled,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField
} from '@mui/material';
import { useEffect, useRef, useState, useMemo } from 'react';
import React, { useContext } from 'react';
import UserContext from 'src/context/UserContext';
import * as yup from 'yup';
import { useFormik } from 'formik';
import Swal from 'sweetalert2';
import { InventoryService } from 'src/services/InventoryService';
import { format, differenceInCalendarDays } from 'date-fns';
import { PaymentStatus, PaymentType } from 'src/models/SubscriptionPlans';
import { PremiumStatus } from 'src/models/User';
import { Role } from 'src/Role';
import { usePlanCatalog } from 'src/features/billing/usePlanCatalog';
import { formatInterval, formatMoney } from 'src/features/billing/format';
import { AdminPlan } from 'src/features/billing/types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

/** Account actions offered next to the plans. */
const REVOKE_ACTION = 'REVOKED';
const EXTEND_ACTION = 'EXTEND';

const needsEndDate = (plan: string) =>
  Boolean(plan) && plan !== REVOKE_ACTION && plan !== EXTEND_ACTION;

const getDefaultEndDate = (plan: AdminPlan | undefined): Dayjs => {
  // Always start-of-day so DatePicker comparisons stay stable
  const base = dayjs().startOf('day');
  const count = plan?.intervalCount ?? 1;
  return plan?.billingInterval === 'YEAR' ? base.add(count, 'year') : base.add(count, 'month');
};

/**
 * Parse payment end dates from shop APIs / table state.
 * App tables primarily use DD/MM/YYYY; APIs may also send ISO.
 */
const parseExistingEndDate = (value: unknown): Dayjs | null => {
  if (value == null || value === '') return null;

  if (dayjs.isDayjs(value)) {
    return value.isValid() ? value.startOf('day') : null;
  }

  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d.startOf('day') : null;
  }

  const raw = String(value).trim();
  if (!raw || raw.toLowerCase() === 'invalid date') return null;

  // Preferred app display format
  const dmy = dayjs(raw, 'DD/MM/YYYY', true);
  if (dmy.isValid()) return dmy.startOf('day');

  const dmyDash = dayjs(raw, 'DD-MM-YYYY', true);
  if (dmyDash.isValid()) return dmyDash.startOf('day');

  // ISO / timestamp
  if (raw.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const iso = dayjs(raw);
    if (iso.isValid()) return iso.startOf('day');
  }

  // Last resort — avoid MM/DD ambiguity for slash dates
  if (raw.includes('/')) {
    return null;
  }

  const fallback = dayjs(raw);
  return fallback.isValid() ? fallback.startOf('day') : null;
};

const resolveInitialPlan = (shop: any): string => {
  if (!shop) return '';
  if (shop.paymentStatus === 'REVOKED') return REVOKE_ACTION;
  if (shop.paymentStatus === 'TRIAL') return EXTEND_ACTION;
  return shop.premiumUserType || shop.previousSubscriptionPlan || '';
};

const resolveInitialEndDate = (
  shop: any,
  plan: string,
  planDetails: AdminPlan | undefined
): Dayjs | null => {
  if (!needsEndDate(plan)) return null;
  return parseExistingEndDate(shop?.paymentEndDate) || getDefaultEndDate(planDetails);
};

const parseReferralCreditsAmount = (data: unknown): number | null => {
  if (data == null) return null;
  if (typeof data === 'number' && !Number.isNaN(data)) return data;

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const keys = [
      'referralCreditsAmount',
      'referralCredits',
      'amount',
      'creditsAmount',
      'availableAmount'
    ];
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'number' && !Number.isNaN(value)) return value;
    }
  }

  return null;
};

function EditPaymentTypePopUp({
  open,
  selectedShop,
  onClose,
  userId,
  allShops,
  setAllShops,
  index
}) {
  const [loading, setLoading] = useState(false);
  const [referralCreditsLoading, setReferralCreditsLoading] = useState(false);
  const [availableReferralCredits, setAvailableReferralCredits] = useState<
    number | null
  >(null);
  const [referralCreditsError, setReferralCreditsError] = useState('');
  const [user] = useContext(UserContext);
  const { plans: catalog, byCode, planName } = usePlanCatalog();

  // Snapshot shop when dialog opens so list search/filter refresh can't corrupt the form
  const [shopSnapshot, setShopSnapshot] = useState<any>(null);
  const [subPlan, setPlan] = useState('');
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const skipNextPickerChange = useRef(false);

  useEffect(() => {
    if (!open) {
      setShopSnapshot(null);
      setPlan('');
      setEndDate(null);
      setAvailableReferralCredits(null);
      setReferralCreditsError('');
      setReferralCreditsLoading(false);
      return;
    }

    // Prefer a plain object shop; ignore accidental array leftovers
    const shop =
      selectedShop && !Array.isArray(selectedShop) ? selectedShop : null;
    if (!shop) return;

    const nextPlan =
      user?.role === Role.CUSTOMER_SUPPORT
        ? EXTEND_ACTION
        : resolveInitialPlan(shop);
    const nextEnd = resolveInitialEndDate(shop, nextPlan, byCode(nextPlan));

    setShopSnapshot(shop);
    setPlan(nextPlan);
    setEndDate(nextEnd);
    skipNextPickerChange.current = true;

    const shopId = shop._id;
    if (!shopId) {
      setAvailableReferralCredits(null);
      setReferralCreditsError('Shop ID not found');
      return;
    }

    let cancelled = false;
    setReferralCreditsLoading(true);
    setReferralCreditsError('');
    setAvailableReferralCredits(null);

    InventoryService.getReferralCreditsAmountByAdminUserId(shopId).then(
      (res) => {
        if (cancelled) return;
        if (res.success) {
          const amount = parseReferralCreditsAmount(res.data);
          if (amount == null) {
            setAvailableReferralCredits(null);
            setReferralCreditsError('Could not read referral credits amount');
          } else {
            setAvailableReferralCredits(amount);
          }
        } else {
          setAvailableReferralCredits(null);
          setReferralCreditsError(res.error || 'Failed to load referral credits');
        }
        setReferralCreditsLoading(false);
      }
    ).catch(() => {
      if (cancelled) return;
      setAvailableReferralCredits(null);
      setReferralCreditsError('Failed to load referral credits');
      setReferralCreditsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // Re-run once the plan catalogue has loaded so default end dates use the plan period.
  }, [open, selectedShop, user?.role, catalog]);

  const validationSchema = useMemo(
    () =>
      yup.object({
        subPlan: yup.string().required('Required'),
        endDate: yup.mixed().when('subPlan', {
          is: (plan: string) => needsEndDate(plan),
          then: yup.mixed().required('Payment end date is required'),
          otherwise: yup.mixed().nullable()
        }),
        referralCreditsDeduction: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue == null ? undefined : value
          )
          .typeError('Enter a valid amount')
          .min(0, 'Amount cannot be negative')
          .test(
            'max-available',
            availableReferralCredits == null
              ? 'Amount exceeds available referral credits'
              : `Amount cannot exceed available credits (${availableReferralCredits.toLocaleString()})`,
            (value) =>
              value == null ||
              availableReferralCredits == null ||
              value <= availableReferralCredits
          )
          .nullable()
      }),
    [availableReferralCredits]
  );

  const formik = useFormik({
    initialValues: {
      subPlan: subPlan || '',
      endDate: endDate,
      referralCreditsDeduction:
        shopSnapshot?.referralCreditsDeduction ??
        selectedShop?.referralCreditsDeduction ??
        ''
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateShop(values);
    }
  });

  const updateShop = (values) => {
    onClose();
    setLoading(true);

    const selectedPlan = values.subPlan;
    const selectedPlanDetails = byCode(selectedPlan);

    // The API records what this customer pays for the plan (including existing-customer pricing).
    const data: Record<string, unknown> = {
      plan: selectedPlan,
      id: userId
    };

    if (needsEndDate(selectedPlan) && values.endDate) {
      data.endDate = dayjs(values.endDate).endOf('day').toISOString();
    }

    if (
      values.referralCreditsDeduction !== '' &&
      values.referralCreditsDeduction != null &&
      !Number.isNaN(Number(values.referralCreditsDeduction))
    ) {
      data.referralCreditsDeduction = Number(values.referralCreditsDeduction);
    }

    InventoryService.updateSubscriptionPlan(data).then((res) => {
      if (res.success) {
        if (res.data.premiumStatus === PremiumStatus.REVOKED) {
          setPlan(PremiumStatus.REVOKED);
          const updatedTemplageProduct = [...allShops];
          updatedTemplageProduct[index] = {
            ...updatedTemplageProduct[index],
            paymentType: PaymentType.BANK,
            paymentStatus: PremiumStatus.REVOKED
          };
          setAllShops(updatedTemplageProduct);
          Swal.fire('Revoked Subscription', 'Successfully', 'success');
        } else {
          const startDateFormat = new Date(res.data.updatedAt);
          const formattedStartDate = format(startDateFormat, 'dd/MM/yyyy');

          if (selectedPlan === EXTEND_ACTION) {
            const updatedTemplageProduct = [...allShops];
            updatedTemplageProduct[index] = {
              ...updatedTemplageProduct[index],
              paymentType: PaymentType.BANK,
              paymentStatus: PaymentStatus.TRIAL,
              remainingDays: 2,
              paymentStartDate: formattedStartDate,
              paymentEndDate: res.data.premiumEndDate
            };
            setAllShops(updatedTemplageProduct);
            Swal.fire('Subscription Updated', 'Successfully', 'success');
          } else {
            const selectedEnd = values.endDate
              ? dayjs(values.endDate).toDate()
              : getDefaultEndDate(selectedPlanDetails).toDate();

            const formattedEndDate = format(selectedEnd, 'dd/MM/yyyy');
            const remainingDays = Math.max(
              differenceInCalendarDays(selectedEnd, new Date()),
              0
            );

            const updatedTemplageProduct = [...allShops];
            updatedTemplageProduct[index] = {
              ...updatedTemplageProduct[index],
              paymentType: PaymentType.BANK,
              paymentStatus: PaymentStatus.PAID,
              remainingDays,
              paymentStartDate: formattedStartDate,
              paymentEndDate: formattedEndDate,
              premiumUserType: selectedPlan,
              previousSubscriptionPlan: selectedPlan
            };
            setAllShops(updatedTemplageProduct);
            Swal.fire('Subscription Updated', 'Successfully', 'success');
          }
        }
        setLoading(false);
      } else {
        setLoading(false);
        Swal.fire({
          icon: 'error',
          title: res.error,
          confirmButtonColor: '#FD7F00'
        });
      }
    });
  };

  const handlesubPlanChange = (event) => {
    const selectedsubPlan = event.target.value;
    setPlan(selectedsubPlan);
    formik.setFieldValue('subPlan', selectedsubPlan);

    if (needsEndDate(selectedsubPlan)) {
      const nextEnd = getDefaultEndDate(byCode(selectedsubPlan));
      skipNextPickerChange.current = true;
      setEndDate(nextEnd);
      formik.setFieldValue('endDate', nextEnd);
    } else {
      setEndDate(null);
      formik.setFieldValue('endDate', null);
    }
  };

  const handleEndDateChange = (value: Dayjs | null) => {
    // Ignore spurious DatePicker onChange when we just set a programmatic default
    if (skipNextPickerChange.current) {
      skipNextPickerChange.current = false;
      return;
    }
    if (!value || !value.isValid()) return;
    const normalized = value.startOf('day');
    setEndDate(normalized);
    formik.setFieldValue('endDate', normalized);
  };

  const shopForForm = shopSnapshot || selectedShop;
  const showEndDate = needsEndDate(subPlan);
  const selectedPlanDetails = byCode(subPlan);

  // Active plans, plus the shop's current plan even if it has since been deactivated.
  const planOptions = catalog.filter((p) => p.isActive || p.code === subPlan);
  const actionOptions = [
    { value: EXTEND_ACTION, label: 'Extend (3-day grace period)' },
    { value: REVOKE_ACTION, label: 'Revoke subscription' }
  ];

  return (
    <BootstrapDialog
      aria-labelledby="customized-dialog-title"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <Typography variant="h4" fontWeight="bold" sx={{ p: 2 }}>
        Edit Payment Plan
      </Typography>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
          <CircularProgress />
        </Box>
      ) : (
        <DialogContent dividers>
          {shopForForm && !Array.isArray(shopForForm) ? (
            <form onSubmit={formik.handleSubmit}>
              <InputLabel sx={{ mb: 1 }}>Select Payment</InputLabel>
              <Select
                labelId="subPlan-label"
                id="subPlan"
                name="subPlan"
                value={subPlan}
                onChange={handlesubPlanChange}
                error={
                  formik.touched.subPlan && Boolean(formik.errors.subPlan)
                }
                sx={{ width: '100%' }}
              >
                {user?.role !== Role.CUSTOMER_SUPPORT &&
                  planOptions.map((plan) => (
                    <MenuItem key={plan.code} value={plan.code}>
                      {plan.name} — {formatMoney(plan.price, plan.currency)}{' '}
                      {formatInterval(plan.billingInterval, plan.intervalCount)}
                      {plan.isActive ? '' : ' (inactive)'}
                    </MenuItem>
                  ))}
                {actionOptions
                  .filter((action) =>
                    user?.role === Role.CUSTOMER_SUPPORT
                      ? action.value === EXTEND_ACTION
                      : true
                  )
                  .map((action) => (
                    <MenuItem key={action.value} value={action.value}>
                      {action.label}
                    </MenuItem>
                  ))}
                {subPlan &&
                  needsEndDate(subPlan) &&
                  !planOptions.some((p) => p.code === subPlan) && (
                    <MenuItem value={subPlan} disabled>
                      {planName(subPlan)} (not in catalogue)
                    </MenuItem>
                  )}
              </Select>
              {formik.touched.subPlan && formik.errors.subPlan && (
                <FormHelperText error>
                  {String(formik.errors.subPlan)}
                </FormHelperText>
              )}

              {showEndDate && (
                <Box sx={{ mt: 2.5 }}>
                  <InputLabel sx={{ mb: 1 }}>Payment End Date</InputLabel>
                  <DatePicker
                    label="End date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    // Do not clamp with minDate — that was forcing "today"
                    // after search/filter when existing/default values raced.
                    inputFormat="DD/MM/YYYY"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={
                          formik.touched.endDate &&
                          Boolean(formik.errors.endDate)
                        }
                        helperText={
                          formik.touched.endDate && formik.errors.endDate
                            ? String(formik.errors.endDate)
                            : `Default: one billing period (${
                                selectedPlanDetails
                                  ? formatInterval(
                                      selectedPlanDetails.billingInterval,
                                      selectedPlanDetails.intervalCount
                                    )
                                  : 'per month'
                              }). Change for a custom duration.`
                        }
                      />
                    )}
                  />
                </Box>
              )}

              <Box sx={{ mt: 2.5 }}>
                <InputLabel sx={{ mb: 1 }}>
                  Referral Credits Deduction
                </InputLabel>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {referralCreditsLoading
                    ? 'Loading available referral credits...'
                    : referralCreditsError
                    ? referralCreditsError
                    : availableReferralCredits != null
                    ? `Available referral credits: ${availableReferralCredits.toLocaleString()}`
                    : 'Available referral credits: —'}
                </Typography>
                <TextField
                  fullWidth
                  name="referralCreditsDeduction"
                  type="number"
                  placeholder="Enter amount"
                  value={formik.values.referralCreditsDeduction}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.referralCreditsDeduction &&
                    Boolean(formik.errors.referralCreditsDeduction)
                  }
                  helperText={
                    formik.touched.referralCreditsDeduction &&
                    formik.errors.referralCreditsDeduction
                      ? String(formik.errors.referralCreditsDeduction)
                      : 'Optional. Amount of referral credits to deduct.'
                  }
                  inputProps={{
                    min: 0,
                    step: 'any',
                    max:
                      availableReferralCredits != null
                        ? availableReferralCredits
                        : undefined
                  }}
                />
              </Box>

              <Box sx={{ height: 20 }} />
              <Button
                color="primary"
                variant="contained"
                type="submit"
                fullWidth
              >
                Update
              </Button>
            </form>
          ) : (
            <Typography variant="body1">Loading...</Typography>
          )}
        </DialogContent>
      )}
    </BootstrapDialog>
  );
}

export default EditPaymentTypePopUp;
